import { Markdown } from '@web/components/Markdown'
import { Icon } from '@web/components/ui/icons'
import { useDialogFocus } from '@web/lib/useDialogFocus'
import { formatDuration } from '@web/state/lifecycle'
import type { ThoughtTrace } from '@web/state/timeline'
import {
  saveTimelinePreferences,
  THINKING_MODES,
  useTimelinePreferences,
} from '@web/state/timeline-preferences'
import {
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

/** A published thought trace is a timeline event, with the transcript a click away. */
export function ReasoningBlock({
  text,
  running,
  traces,
  latest = true,
}: {
  text: string
  running?: boolean
  traces?: ThoughtTrace[]
  latest?: boolean
}) {
  const { thinkingMode } = useTimelinePreferences()
  const defaultOpen =
    thinkingMode === 'verbose' || (thinkingMode === 'balanced' && latest)
  const [open, setOpen] = useState(defaultOpen)
  const [menu, setMenu] = useState(false)
  const [now, setNow] = useState(Date.now() / 1000)
  const [visible, setVisible] = useState(document.visibilityState !== 'hidden')
  const contentId = useId()
  const optionsRef = useRef<HTMLButtonElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const press = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const held = useRef(false)
  const entries = traces?.length ? traces : [{ id: contentId, text }]
  const current = entries.at(-1) ?? { id: contentId, text }
  useEffect(
    () =>
      setOpen(
        thinkingMode === 'verbose' || (thinkingMode === 'balanced' && latest),
      ),
    [thinkingMode, latest],
  )
  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', update)
    return () => document.removeEventListener('visibilitychange', update)
  }, [])
  useEffect(() => {
    if (!running || !visible) return
    setNow(Date.now() / 1000)
    const timer = setInterval(() => setNow(Date.now() / 1000), 1000)
    return () => clearInterval(timer)
  }, [running, visible])
  useEffect(() => () => clearTimeout(press.current), [])
  const seconds = entries.reduce(
    (total, entry) =>
      total +
      (entry.startedAt === undefined
        ? 0
        : Math.max(
            0,
            (entry.endedAt ?? (running ? now : entry.startedAt)) -
              entry.startedAt,
          )),
    0,
  )
  const duration = entries.some((entry) => entry.startedAt !== undefined)
    ? ` for ${seconds < 1 ? '<1s' : formatDuration(seconds)}`
    : ''
  const preview = current.text.replace(/\s+/g, ' ').replace(/[*#`]/g, '').trim()
  const stopPress = () => clearTimeout(press.current)
  return (
    <div
      className={`thought-event ${running && visible ? 'is-working' : ''}`}
      data-testid="thought-event"
      data-mode={thinkingMode}
    >
      <div className="thought-heading">
        <button
          ref={toggleRef}
          type="button"
          className="thought-toggle"
          aria-expanded={open}
          aria-controls={open ? contentId : undefined}
          onClick={() => {
            if (held.current) {
              held.current = false
              return
            }
            setOpen((value) => !value)
          }}
          onContextMenu={(event) => {
            event.preventDefault()
            setMenu(true)
          }}
          onKeyDown={(event) => {
            if (
              event.key === 'ContextMenu' ||
              (event.shiftKey && event.key === 'F10')
            ) {
              event.preventDefault()
              setMenu(true)
            }
          }}
          onPointerDown={(event) => {
            if (event.pointerType === 'mouse') return
            held.current = false
            press.current = setTimeout(() => {
              held.current = true
              setMenu(true)
            }, 500)
          }}
          onPointerUp={stopPress}
          onPointerCancel={stopPress}
          onPointerLeave={stopPress}
        >
          <Icon name="thinking" size={17} still />
          <span className="thought-label">
            {running ? 'Thinking' : 'Thought'}
            {duration}
          </span>
          {!open && <span className="thought-preview">{preview || '…'}</span>}
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={13} still />
        </button>
        <button
          ref={optionsRef}
          type="button"
          className="thought-options"
          aria-label="Thought trace display"
          aria-haspopup="dialog"
          aria-expanded={menu}
          onClick={() => setMenu((value) => !value)}
        >
          <Icon name="more" size={14} />
        </button>
      </div>
      {menu && (
        <ThoughtPreferences
          anchor={optionsRef}
          onClose={() => setMenu(false)}
        />
      )}
      {open && (
        <div id={contentId} className="thought-transcript">
          {entries.map((entry) => (
            <div key={entry.id} className="thought-trace">
              <Markdown text={entry.text || '…'} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** A modal popover anchored to the options button, outside the transcript layout. */
function ThoughtPreferences({
  anchor,
  onClose,
}: {
  anchor: RefObject<HTMLButtonElement | null>
  onClose: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const { thinkingMode } = useTimelinePreferences()
  useDialogFocus(dialog, onClose, true, anchor.current)
  useLayoutEffect(() => {
    const element = dialog.current
    if (!element) return
    element.showModal()
    const place = () => {
      const bounds = anchor.current?.getBoundingClientRect()
      if (!bounds) return
      const width = element.offsetWidth
      const height = element.offsetHeight
      const below = window.innerHeight - bounds.bottom - 8
      const top = below >= height ? bounds.bottom + 6 : bounds.top - height - 6
      element.style.left = `${Math.max(8, Math.min(bounds.right - width, window.innerWidth - width - 8))}px`
      element.style.top = `${Math.max(8, Math.min(top, window.innerHeight - height - 8))}px`
    }
    place()
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('resize', place)
      element.close()
    }
  }, [anchor])
  useEffect(() => {
    dialog.current
      ?.querySelector<HTMLButtonElement>('[aria-checked="true"]')
      ?.focus()
  }, [])
  return createPortal(
    <dialog
      ref={dialog}
      className="thought-preferences"
      aria-label="Default thought trace display"
      onCancel={onClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose()
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return
        const bounds = event.currentTarget.getBoundingClientRect()
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          onClose()
      }}
    >
      <div className="thought-preferences-title">
        Default thought trace display
      </div>
      <div
        role="radiogroup"
        aria-label="Thought trace display mode"
        onKeyDown={(event) => {
          const choices = Array.from(
            dialog.current?.querySelectorAll<HTMLButtonElement>(
              '[role="radio"]',
            ) ?? [],
          )
          const at = choices.indexOf(
            document.activeElement as HTMLButtonElement,
          )
          if (
            [
              'ArrowDown',
              'ArrowRight',
              'ArrowUp',
              'ArrowLeft',
              'Home',
              'End',
            ].includes(event.key)
          ) {
            event.preventDefault()
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? choices.length - 1
                  : (at +
                      (['ArrowDown', 'ArrowRight'].includes(event.key)
                        ? 1
                        : -1) +
                      choices.length) %
                    choices.length
            choices[next]?.focus()
          }
        }}
      >
        {THINKING_MODES.map((mode) => (
          <button
            key={mode.value}
            type="button"
            role="radio"
            aria-checked={thinkingMode === mode.value}
            onClick={() => {
              saveTimelinePreferences({ thinkingMode: mode.value })
              onClose()
            }}
          >
            <span>
              {mode.label}
              {mode.value === 'compact' ? ' (Default)' : ''}
            </span>
            <small>{mode.description}</small>
          </button>
        ))}
      </div>
    </dialog>,
    document.body,
  )
}
